import { Direction } from '@angular/cdk/bidi';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';
import { IconType } from '@ng-icons/core';

@Component({
    selector: 'mot-input',
    templateUrl: './mot-input.component.html',
    styleUrls: ['./mot-input.component.scss'],
    standalone: false
})
export class MotInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Output() onIconClick: EventEmitter<MouseEvent> =
    new EventEmitter<MouseEvent>();
  @Output() onClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() onTextChange: EventEmitter<Event> = new EventEmitter<Event>();

  @Input() displayErrors: boolean = true;
  @Input() floatLabelType: FloatLabelType = 'auto';
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() label: string;
  @Input() placeholder: string;
  @Input() value: any;
  @Input() type: string;
  @Input() pattern: string | RegExp;
  @Input() class: string;
  @Input() inputClass: string;
  @Input() readOnly: boolean = false;

  @Input() prefixIconClass: string;
  @Input() prefixIcon: IconType;

  @Input() iconClass: string;
  @Input() icon: IconType;
  /** Accessibility: aria-label for the suffix icon button (e.g. Clear). */
  @Input() iconButtonAriaLabel: string;

  @Input() dir: Direction

  @Input() autoFocus: boolean = false;

  /**
   * a11y/combobox-autocomplete-list pattern (WAI-ARIA APG):
   *   https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/
   *
   * When `comboboxRole` is true, the inner <input> is exposed to assistive tech as a
   * combobox that filters a separately-rendered listbox. The parent owns the listbox
   * DOM and keyboard navigation; this component just forwards the required attributes.
   *
   * - `comboboxControlsId`: id of the listbox element this combobox controls.
   * - `comboboxExpanded`: true when the listbox is currently visible with options.
   * - `comboboxActiveDescendant`: id of the option that's the virtual-focus target
   *   while DOM focus stays on the input (or null when nothing is highlighted).
   * - `ariaLabel`: explicit accessible name when no <mat-label> is rendered.
   */
  @Input() comboboxRole: boolean = false;
  @Input() comboboxControlsId: string;
  @Input() comboboxExpanded: boolean = false;
  @Input() comboboxActiveDescendant: string | null = null;
  @Input() ariaLabel: string;

  formControl: FormControl;

  /** Unique id for error message (aria-describedby link). */
  errorId = 'mot-input-err-' + Math.random().toString(36).slice(2, 11);

  @ViewChild('inputElement') inputElement: ElementRef;

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
      if (this.value) {
        this.formControl.setValue(this.value);
      }
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!!this.formControl) {
      if (isDisabled) this.formControl.disable();
      else this.formControl.enable();
    }
  }

  getFirstError(): string {
    const errors = this.formControl.errors;

    return errors[Object.keys(errors)[0]];
  }

  ngAfterViewInit() {
    if (this.autoFocus && this.inputElement) {
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 0);
    }
  }

  /** Imperative focus for parents managing combobox state (e.g. after option pick). */
  focusInput(): void {
    const el = this.inputElement?.nativeElement;
    if (el && !el.disabled) {
      el.focus();
    }
  }
}
